import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Heart, Tag, CalendarDays, Flag, ArrowRight, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { api, useAuth } from '../lib/auth';

const STEPS = [
  { icon: Flag, kicker: 'Welcome aboard', title: 'Your garage starts here', body: 'A quick lap around AutoMobile. Takes 20 seconds, and you can skip anytime.' },
  { icon: Search, kicker: 'Browse', title: 'Find your next car', body: 'Filter the marketplace by make, price and drivetrain. Press ⌘K (Ctrl+K) anywhere to search instantly.', cta: { label: 'Open marketplace', to: '/marketplace' } },
  { icon: Heart, kicker: 'Watchlist', title: 'Save it, track the price', body: 'Tap the heart on any listing. We notify you the moment its price drops.', cta: { label: 'View watchlist', to: '/watchlist' } },
  { icon: Tag, kicker: 'Sell', title: 'List your ride in minutes', body: 'Upload photos, add specs and set your price. Buyers message you directly.', cta: { label: 'Start a listing', to: '/sell' } },
  { icon: CalendarDays, kicker: 'Events & maps', title: 'Meet the community', body: 'RSVP to meets and track days, or host your own and message everyone going.', cta: { label: 'See events', to: '/events' } },
];

export default function WelcomeTour() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const open = !!user?.tour_pending;

  const finish = async (to) => {
    setUser({ ...user, tour_pending: false });
    try { await api.post('/auth/tour-complete'); } catch {}
    if (to) navigate(to);
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const last = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent data-testid="welcome-tour" className="bg-[#0b0b0c] border border-zinc-800 text-white rounded-none sm:rounded-none max-w-md p-0 overflow-hidden">
        <div className="h-1 bg-zinc-900">
          <div className="h-full bg-[#ff2a2a] transition-[width] duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} data-testid="welcome-tour-progress" />
        </div>
        <div key={step} className="p-8 am-route-in">
          <div className="w-12 h-12 grid place-items-center bg-[#ff2a2a]/10 border border-[#ff2a2a]/40">
            <Icon className="w-6 h-6 text-[#ff2a2a]" />
          </div>
          <div className="mt-6 font-mono text-xs uppercase tracking-widest text-[#ff2a2a]" data-testid="welcome-tour-step-label">
            {String(step + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')} · {s.kicker}
          </div>
          <DialogTitle className="mt-2 font-display text-3xl uppercase tracking-tight" data-testid="welcome-tour-title">{s.title}</DialogTitle>
          <DialogDescription className="mt-3 text-sm text-zinc-400 leading-relaxed">{s.body}</DialogDescription>
          {s.cta && (
            <button onClick={() => finish(s.cta.to)} className="mt-4 text-sm text-white underline underline-offset-4 decoration-[#ff2a2a] hover:text-[#ff2a2a] transition-colors" data-testid="welcome-tour-cta">
              {s.cta.label}
            </button>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-zinc-800 px-8 py-4">
          <button onClick={() => finish()} className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors" data-testid="welcome-tour-skip">Skip tour</button>
          <div className="flex gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="p-2.5 border border-zinc-700 hover:border-white transition-colors" aria-label="Back" data-testid="welcome-tour-back">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => (last ? finish('/marketplace') : setStep(step + 1))} className="flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-5 py-2.5 transition-colors" data-testid="welcome-tour-next">
              {last ? 'Start exploring' : 'Next'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
